import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Lightbulb, User, CalendarDays, ArrowDownRight, Send, MessageSquare, CheckCircle2, PenLine, Clock, Eye, FileText as FileTextIcon, Receipt, ClipboardCheck, Rocket, ShieldCheck, Check, Circle, Sparkles, FileText, ChevronDown, Bell, AlertCircle, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { GpBadge } from './GpBadge';
import { SourceAnnotation } from './SourceAnnotation';
interface Milestone {
  date: string;
  description: string;
  entity: string;
  monthLabel?: string;
  isTBD?: boolean;
}
interface NextAction {
  gp: string;
  tasks: Array<{
    id: string;
    action: string;
    owner: string | null;
    deadline: string | null;
    dependsOnTaskId?: string;
    partnership?: string;
  }>;
}
type WorkstreamStatus = 'not_started' | 'at_risk' | 'in_progress' | 'complete';
interface WorkstreamPart {
  name: string;
  complete: boolean;
}
interface WorkstreamCell {
  status: WorkstreamStatus;
  label: string;
  parts?: WorkstreamPart[];
}
interface ProjectOverviewData {
  summaryHeading: string;
  summary: string;
  months: string[];
  currentMonthIndex: number;
  milestones: Milestone[];
  nextActions: NextAction[];
  workstreams: {
    columns: string[];
    rows: Array<{
      gp: string;
      cells: WorkstreamCell[];
    }>;
  };
}
export const MOCK_DATA: Record<string, ProjectOverviewData> = {
  Diamond: {
    summaryHeading: 'Overall Status',
    summary: 'Portfolio execution remains at an early-to-mid stage. PSA drafting is advancing with an updated draft circulated for several seller vehicles requiring BlackRock confirmations on key commercial terms. One GP has reached executed consent stage, while most others have not yet initiated TA drafting or KYC/AML workstreams. Principal gating factors include outstanding DDQ responses, ROFO process coordination, buyer approval requirements, and pending K-1 delivery for withholding analysis.',
    months: ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov'],
    currentMonthIndex: 0,
    milestones: [{
      date: 'Oct 15, 2026',
      description: 'Proposed Final Closing Deadline per draft PSA',
      entity: 'Diamond portfolio sellers',
      monthLabel: 'Oct'
    }, {
      date: 'Date TBD',
      description: '60-day LP ROFO window applies to Amports co-investment',
      entity: 'InstarAGF',
      isTBD: true
    }],
    nextActions: [{
      gp: 'EQT',
      tasks: [{
        id: 'eqt-1',
        action: 'Obtain non-tax DDQ responses (purpose of AIV, 754 election, PFIC status, ERISA status)',
        owner: 'EQT GP counsel',
        deadline: 'Apr 28, 2026',
        partnership: 'EQT Infrastructure III Co-Investment (A) SCSp'
      }, {
        id: 'eqt-2',
        action: 'Circulate PTP questionnaire to GP',
        owner: 'Akin Gump',
        deadline: null,
        partnership: 'EQT Infrastructure III Co-Investment (A) SCSp'
      }, {
        id: 'eqt-3',
        action: 'Confirm KYC/AML requirements with fund administrator',
        owner: 'Fund administrator',
        deadline: null,
        partnership: 'EQT Infrastructure III Co-Investment (A) SCSp'
      }, {
        id: 'eqt-4',
        action: 'Initiate TA drafting',
        owner: 'Akin Gump',
        deadline: 'Apr 30, 2026',
        dependsOnTaskId: 'eqt-1',
        partnership: 'EQT Infrastructure III Co-Investment (A) SCSp'
      }]
    }, {
      gp: 'Brookfield',
      tasks: [{
        id: 'brook-1',
        action: 'Provide DDQ Tax section responses',
        owner: 'Brookfield / Weil',
        deadline: 'Apr 21, 2026',
        partnership: 'Brookfield Infrastructure Fund II, L.P.'
      }, {
        id: 'brook-2',
        action: 'Confirm willingness to provide 1446 certificates and ECI/FIRPTA status',
        owner: 'Brookfield',
        deadline: null,
        dependsOnTaskId: 'brook-1',
        partnership: 'Brookfield Infrastructure Fund II, L.P.'
      }, {
        id: 'brook-3',
        action: 'Initiate TA drafting',
        owner: 'Akin Gump',
        deadline: null,
        dependsOnTaskId: 'brook-2',
        partnership: 'Brookfield Infrastructure Fund II, L.P.'
      }]
    }, {
      gp: 'InstarAGF',
      tasks: [{
        id: 'instar-1',
        action: 'Clarify BlackRock position on Instar holdings before resuming workstreams',
        owner: 'BlackRock / Seller counsel',
        deadline: null
      }, {
        id: 'instar-2',
        action: 'Resolve ROFO process timeline for Amports co-investment',
        owner: 'Seller counsel',
        deadline: null,
        dependsOnTaskId: 'instar-1'
      }, {
        id: 'instar-3',
        action: 'Initiate TA drafting and KYC/AML onboarding',
        owner: 'Akin Gump',
        deadline: null,
        dependsOnTaskId: 'instar-1'
      }]
    }, {
      gp: 'BlackRock',
      tasks: [{
        id: 'br-1',
        action: 'Deliver K-1s for withholding analysis (BPI II, GIS 3, ASGA, Total Alternatives, BIS Z)',
        owner: 'BlackRock tax team',
        deadline: 'Apr 19, 2026'
      }, {
        id: 'br-2',
        action: 'Initiate TA drafting for all 5 BlackRock positions',
        owner: 'Akin Gump',
        deadline: null,
        dependsOnTaskId: 'br-1'
      }]
    }],
    workstreams: {
      columns: ['TA', 'Onboarding', 'Diligence'],
      rows: [{
        gp: 'Antin Infrastructure Partners',
        cells: [{
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }]
      }, {
        gp: 'Ardian',
        cells: [{
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }]
      }, {
        gp: 'Asterion Industrial Partners',
        cells: [{
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }]
      }, {
        gp: 'BlackRock',
        cells: [{
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'in_progress',
          label: 'Pending',
          parts: [{
            name: 'DDQ Responses',
            complete: true
          }, {
            name: 'Tax Withholding',
            complete: false
          }, {
            name: 'ROFO',
            complete: false
          }]
        }]
      }, {
        gp: 'Brookfield',
        cells: [{
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'at_risk',
          label: 'Blocked',
          parts: [{
            name: 'DDQ Responses',
            complete: false
          }, {
            name: 'Tax Withholding',
            complete: false
          }]
        }]
      }, {
        gp: 'DIF Capital Partners',
        cells: [{
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }]
      }, {
        gp: 'DigitalBridge',
        cells: [{
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }]
      }, {
        gp: 'EQT',
        cells: [{
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'at_risk',
          label: 'Blocked',
          parts: [{
            name: 'DDQ Responses',
            complete: false
          }, {
            name: 'ERISA Reps',
            complete: false
          }, {
            name: 'Side Letter Assignment',
            complete: false
          }]
        }]
      }, {
        gp: 'InfraVia Capital Partners',
        cells: [{
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }]
      }, {
        gp: 'InstarAGF Asset Management',
        cells: [{
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'at_risk',
          label: 'Blocked',
          parts: [{
            name: 'DDQ Responses',
            complete: false
          }, {
            name: 'ROFO',
            complete: false
          }, {
            name: 'Tax Withholding',
            complete: false
          }]
        }]
      }, {
        gp: 'KKR',
        cells: [{
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }]
      }, {
        gp: 'Macquarie',
        cells: [{
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }]
      }, {
        gp: 'Northleaf Capital Partners',
        cells: [{
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }]
      }]
    }
  },
  Cascade: {
    summaryHeading: 'Overall Status',
    summary: 'Large-scale LP interest sale progressing across 50 GP relationships. Transfer agreements are advancing for 12 GPs, with 8 fully executed and 6 currently blocked on KYC/AML or DDQ responses. Onboarding is complete for 15 GPs. Key gating items include outstanding tax withholding confirmations, ERISA representations, and buyer entity confirmations for several large-cap managers.',
    months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug'],
    currentMonthIndex: 2,
    milestones: [{
      date: 'May 30, 2026',
      description: 'Target closing date for first tranche',
      entity: 'Redwood Capital Partners',
      monthLabel: 'May'
    }, {
      date: 'Jul 15, 2026',
      description: 'Final closing deadline per PSA',
      entity: 'All GPs',
      monthLabel: 'Jul'
    }],
    nextActions: [{
      gp: 'Apollo Global',
      tasks: [{
        id: 'cas-1',
        action: 'Review partial DDQ responses from GP counsel',
        owner: 'Hastings & Cole',
        deadline: 'Mar 20, 2026'
      }, {
        id: 'cas-2',
        action: 'Confirm TA execution timeline',
        owner: 'Apollo legal',
        deadline: 'Mar 25, 2026',
        dependsOnTaskId: 'cas-1'
      }]
    }, {
      gp: 'Blackstone',
      tasks: [{
        id: 'cas-3',
        action: 'Initiate TA drafting for Real Estate Fund VII',
        owner: 'Bridgeport Legal',
        deadline: null
      }]
    }, {
      gp: 'Carlyle',
      tasks: [{
        id: 'cas-4',
        action: 'Obtain DDQ responses for Asia Partners Fund',
        owner: 'Carlyle counsel',
        deadline: 'Apr 5, 2026'
      }, {
        id: 'cas-5',
        action: 'Confirm buyer entity for Europe Fund V',
        owner: 'Buyer',
        deadline: null
      }]
    }, {
      gp: 'KKR',
      tasks: [{
        id: 'cas-6',
        action: 'Deliver K-1s for withholding analysis',
        owner: 'KKR tax team',
        deadline: 'Mar 28, 2026'
      }]
    }, {
      gp: 'Warburg Pincus',
      tasks: [{
        id: 'cas-7',
        action: 'Complete KYC/AML review for transferee',
        owner: 'Fund administrator',
        deadline: null
      }]
    }, {
      gp: 'TPG',
      tasks: [{
        id: 'cas-8',
        action: 'Confirm ERISA representations',
        owner: 'Buyer counsel',
        deadline: 'Apr 10, 2026'
      }]
    }, {
      gp: 'Advent International',
      tasks: [{
        id: 'cas-9',
        action: 'Circulate TA execution version',
        owner: 'Hastings & Cole',
        deadline: null
      }]
    }, {
      gp: 'Silver Lake',
      tasks: [{
        id: 'cas-10',
        action: 'Settle outstanding invoice prior to closing',
        owner: 'Buyer',
        deadline: 'Apr 15, 2026'
      }]
    }],
    workstreams: {
      columns: ['TA', 'Onboarding', 'Diligence'],
      rows: [{
        gp: 'Apollo Global Management',
        cells: [{
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'complete',
          label: 'Complete'
        }]
      }, {
        gp: 'Ares Management',
        cells: [{
          status: 'in_progress',
          label: 'Drafting'
        }, {
          status: 'in_progress',
          label: 'Pending'
        }, {
          status: 'complete',
          label: 'Complete'
        }]
      }, {
        gp: 'Bain Capital',
        cells: [{
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'complete',
          label: 'Complete'
        }]
      }, {
        gp: 'Blackstone',
        cells: [{
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'in_progress',
          label: 'Pending'
        }]
      }, {
        gp: 'Brookfield Asset Management',
        cells: [{
          status: 'in_progress',
          label: 'Drafting'
        }, {
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'at_risk',
          label: 'Blocked'
        }]
      }, {
        gp: 'Carlyle Group',
        cells: [{
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'in_progress',
          label: 'Pending'
        }, {
          status: 'complete',
          label: 'Complete'
        }]
      }, {
        gp: 'CVC Capital Partners',
        cells: [{
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }]
      }, {
        gp: 'EQT Partners',
        cells: [{
          status: 'in_progress',
          label: 'Pending'
        }, {
          status: 'in_progress',
          label: 'Subscribe'
        }, {
          status: 'complete',
          label: 'Complete'
        }]
      }, {
        gp: 'Francisco Partners',
        cells: [{
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'complete',
          label: 'Complete'
        }]
      }, {
        gp: 'General Atlantic',
        cells: [{
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }]
      }, {
        gp: 'Hellman & Friedman',
        cells: [{
          status: 'in_progress',
          label: 'Drafting'
        }, {
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'complete',
          label: 'Complete'
        }]
      }, {
        gp: 'Hg Capital',
        cells: [{
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'at_risk',
          label: 'Blocked'
        }]
      }, {
        gp: 'Insight Partners',
        cells: [{
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'in_progress',
          label: 'Pending'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }]
      }, {
        gp: 'KKR & Co',
        cells: [{
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'complete',
          label: 'Complete'
        }]
      }, {
        gp: 'Leonard Green & Partners',
        cells: [{
          status: 'in_progress',
          label: 'Pending'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'in_progress',
          label: 'Pending'
        }]
      }, {
        gp: 'Lone Star Funds',
        cells: [{
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }]
      }, {
        gp: 'Madison Dearborn Partners',
        cells: [{
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'in_progress',
          label: 'Pending'
        }, {
          status: 'complete',
          label: 'Complete'
        }]
      }, {
        gp: 'New Mountain Capital',
        cells: [{
          status: 'in_progress',
          label: 'Drafting'
        }, {
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'in_progress',
          label: 'Pending'
        }]
      }, {
        gp: 'Nordic Capital',
        cells: [{
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }]
      }, {
        gp: 'Permira',
        cells: [{
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'complete',
          label: 'Complete'
        }]
      }, {
        gp: 'Providence Equity Partners',
        cells: [{
          status: 'in_progress',
          label: 'Pending'
        }, {
          status: 'at_risk',
          label: 'Blocked'
        }, {
          status: 'in_progress',
          label: 'Pending'
        }]
      }, {
        gp: 'Silver Lake Partners',
        cells: [{
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'complete',
          label: 'Complete'
        }]
      }, {
        gp: 'Summit Partners',
        cells: [{
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'in_progress',
          label: 'Pending'
        }]
      }, {
        gp: 'TA Associates',
        cells: [{
          status: 'in_progress',
          label: 'Drafting'
        }, {
          status: 'in_progress',
          label: 'Subscribe'
        }, {
          status: 'complete',
          label: 'Capable'
        }]
      }, {
        gp: 'Thoma Bravo',
        cells: [{
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'complete',
          label: 'Complete'
        }]
      }, {
        gp: 'TPG Capital',
        cells: [{
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'at_risk',
          label: 'Blocked'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }]
      }, {
        gp: 'Veritas Capital',
        cells: [{
          status: 'in_progress',
          label: 'Pending'
        }, {
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'complete',
          label: 'Complete'
        }]
      }, {
        gp: 'Vista Equity Partners',
        cells: [{
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'in_progress',
          label: 'Pending'
        }, {
          status: 'complete',
          label: 'Complete'
        }]
      }, {
        gp: 'Warburg Pincus',
        cells: [{
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }]
      }, {
        gp: 'Welsh Carson Anderson & Stowe',
        cells: [{
          status: 'in_progress',
          label: 'Drafting'
        }, {
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'in_progress',
          label: 'Pending'
        }]
      }, {
        gp: 'Advent International',
        cells: [{
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'complete',
          label: 'Complete'
        }]
      }, {
        gp: 'Apax Partners',
        cells: [{
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }]
      }, {
        gp: 'Bridgepoint',
        cells: [{
          status: 'in_progress',
          label: 'Pending'
        }, {
          status: 'in_progress',
          label: 'Pending'
        }, {
          status: 'complete',
          label: 'Complete'
        }]
      }, {
        gp: 'Cinven',
        cells: [{
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'at_risk',
          label: 'Blocked'
        }]
      }, {
        gp: 'Clayton Dubilier & Rice',
        cells: [{
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }]
      }, {
        gp: 'Coller Capital',
        cells: [{
          status: 'in_progress',
          label: 'Drafting'
        }, {
          status: 'in_progress',
          label: 'Subscribe'
        }, {
          status: 'in_progress',
          label: 'Pending'
        }]
      }, {
        gp: 'Dyal Capital Partners',
        cells: [{
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'complete',
          label: 'Complete'
        }]
      }, {
        gp: 'Genstar Capital',
        cells: [{
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'in_progress',
          label: 'Pending'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }]
      }, {
        gp: 'GI Partners',
        cells: [{
          status: 'in_progress',
          label: 'Pending'
        }, {
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'complete',
          label: 'Complete'
        }]
      }, {
        gp: 'GTCR',
        cells: [{
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'complete',
          label: 'Complete'
        }]
      }, {
        gp: 'Harvest Partners',
        cells: [{
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }]
      }, {
        gp: 'ICG',
        cells: [{
          status: 'in_progress',
          label: 'Drafting'
        }, {
          status: 'at_risk',
          label: 'Blocked'
        }, {
          status: 'in_progress',
          label: 'Pending'
        }]
      }, {
        gp: 'Kelso & Company',
        cells: [{
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'in_progress',
          label: 'Pending'
        }]
      }, {
        gp: 'Lexington Partners',
        cells: [{
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }]
      }, {
        gp: 'MBK Partners',
        cells: [{
          status: 'in_progress',
          label: 'Pending'
        }, {
          status: 'in_progress',
          label: 'Pending'
        }, {
          status: 'complete',
          label: 'Complete'
        }]
      }, {
        gp: 'Montagu Private Equity',
        cells: [{
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'complete',
          label: 'Complete'
        }]
      }, {
        gp: 'PAI Partners',
        cells: [{
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'at_risk',
          label: 'Blocked'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }]
      }, {
        gp: 'Partners Group',
        cells: [{
          status: 'in_progress',
          label: 'Drafting'
        }, {
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'complete',
          label: 'Complete'
        }]
      }, {
        gp: 'Platinum Equity',
        cells: [{
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'in_progress',
          label: 'Pending'
        }, {
          status: 'complete',
          label: 'Complete'
        }]
      }, {
        gp: 'Roark Capital Group',
        cells: [{
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }]
      }]
    }
  },
  // Default data for other projects based on the old ProjectOverview content
  default: {
    summaryHeading: 'Overall Status',
    summary: 'Buyer and seller counsel advanced documentation and execution readiness across multiple funds. Stratford moved into payment and closing release, while Kingston and Waterloo progressed toward final TA circulation. No new execution blockers were identified.',
    months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug'],
    currentMonthIndex: 1,
    milestones: [{
      date: 'Feb 14, 2026',
      description: 'GP deadline to finalize transfer agreement',
      entity: 'Waterloo Capital',
      monthLabel: 'Feb'
    }, {
      date: 'Feb 18, 2026',
      description: 'Deadline to confirm final transferee list and launch GP KYC/AML review',
      entity: 'Niagara Growth',
      monthLabel: 'Feb'
    }, {
      date: 'Mar 12, 2026',
      description: 'Invoice settlement deadline to permit closing release',
      entity: 'Stratford Credit',
      monthLabel: 'Mar'
    }, {
      date: 'Mar 14, 2026',
      description: 'Deadline to complete the KYC/AML process',
      entity: 'Gatineau Real Assets',
      monthLabel: 'Mar'
    }],
    nextActions: [{
      gp: 'Stratford Credit',
      tasks: [{
        id: 'strat-1',
        action: 'Settle invoice prior to release of executed documentation',
        owner: 'Buyer',
        deadline: 'Mar 12, 2026'
      }]
    }, {
      gp: 'Laval Global',
      tasks: [{
        id: 'laval-1',
        action: 'Complete routine post-closing circulation',
        owner: 'Administrator',
        deadline: null
      }]
    }, {
      gp: 'Maple Tree',
      tasks: [{
        id: 'maple-1',
        action: 'Complete remaining administrative items',
        owner: 'Seller counsel',
        deadline: null
      }]
    }, {
      gp: 'Waterloo Capital',
      tasks: [{
        id: 'wat-1',
        action: 'Launch GP consent',
        owner: 'GP',
        deadline: null
      }, {
        id: 'wat-2',
        action: 'Complete KYC',
        owner: 'Buyer',
        deadline: null
      }, {
        id: 'wat-3',
        action: 'Circulate TA',
        owner: 'Seller counsel',
        deadline: null,
        dependsOnTaskId: 'wat-1'
      }]
    }],
    workstreams: {
      columns: ['TA', 'Onboarding', 'Diligence'],
      rows: [{
        gp: 'Collingwood Energy',
        cells: [{
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'complete',
          label: 'Complete'
        }]
      }, {
        gp: 'Gatineau Real Assets',
        cells: [{
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'in_progress',
          label: 'Pending',
          parts: [{
            name: 'KYC/AML',
            complete: false
          }, {
            name: 'Sub Docs',
            complete: false
          }, {
            name: 'Tax Forms',
            complete: false
          }]
        }, {
          status: 'complete',
          label: 'Complete'
        }]
      }, {
        gp: 'Kingston Partners',
        cells: [{
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }]
      }, {
        gp: 'Laval Global',
        cells: [{
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'complete',
          label: 'Complete'
        }]
      }, {
        gp: 'Maple Tree',
        cells: [{
          status: 'in_progress',
          label: 'Drafting',
          parts: [{
            name: 'Drafted',
            complete: true
          }, {
            name: 'Invoices',
            complete: false
          }, {
            status: 'Signatures in Escrow',
            complete: false
          }]
        }, {
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'complete',
          label: 'Complete'
        }]
      }, {
        gp: 'Niagara Growth',
        cells: [{
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'at_risk',
          label: 'Blocked',
          parts: [{
            name: 'KYC/AML',
            complete: false
          }, {
            name: 'Sub Docs',
            complete: false
          }, {
            name: 'Tax Forms',
            complete: false
          }]
        }, {
          status: 'in_progress',
          label: 'Pending',
          parts: [{
            name: 'DDQ Responses',
            complete: false
          }, {
            name: 'ERISA Reps',
            complete: false
          }, {
            name: 'Side Letter Assignment',
            complete: false
          }]
        }]
      }, {
        gp: 'Sherbrooke Capital Partners',
        cells: [{
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'in_progress',
          label: 'Pending',
          parts: [{
            name: 'DDQ Responses',
            complete: true
          }, {
            name: 'Tax Withholding',
            complete: false
          }]
        }]
      }, {
        gp: 'Stratford Credit',
        cells: [{
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'complete',
          label: 'Complete'
        }, {
          status: 'complete',
          label: 'Complete'
        }]
      }, {
        gp: 'Waterloo Capital',
        cells: [{
          status: 'not_started',
          label: 'Not Started'
        }, {
          status: 'in_progress',
          label: 'Submitted',
          parts: [{
            name: 'KYC/AML',
            complete: true
          }, {
            name: 'Sub Docs',
            complete: true
          }, {
            name: 'Tax Forms',
            complete: false
          }]
        }, {
          status: 'complete',
          label: 'Complete'
        }]
      }]
    }
  }
};
export function computeGpConsentFromWorkstreams(projectName: string, gpName: string): {
  status: string;
  label: string;
} {
  const data = MOCK_DATA[projectName] || MOCK_DATA.default;
  if (!data) return {
    status: 'not_started',
    label: 'Consent Not Requested'
  };
  const row = data.workstreams.rows.find(r => r.gp === gpName);
  if (!row) return {
    status: 'not_started',
    label: 'Consent Not Requested'
  };
  const isAllComplete = row.cells.every(c => c.status === 'complete');
  const isAnyAtRisk = row.cells.some(c => c.status === 'at_risk');
  const isAllNotStarted = row.cells.every(c => c.status === 'not_started');
  if (isAllComplete) return {
    status: 'complete',
    label: 'Consent Granted'
  };
  if (isAnyAtRisk) return {
    status: 'at_risk',
    label: 'Consent Blocked'
  };
  if (isAllNotStarted) return {
    status: 'not_started',
    label: 'Consent Not Requested'
  };
  return {
    status: 'in_progress',
    label: 'Consent Pending'
  };
}
function getActionIcon(action: string) {
  const a = action.toLowerCase();
  if (a.includes('deliver') || a.includes('circulat')) return <Send className="w-4 h-4" data-id="element-537" />;
  if (a.includes('obtain') || a.includes('request') || a.includes('provide')) return <MessageSquare className="w-4 h-4" data-id="element-538" />;
  if (a.includes('confirm') || a.includes('accept')) return <CheckCircle2 className="w-4 h-4" data-id="element-539" />;
  if (a.includes('draft') || a.includes('initiate')) return <PenLine className="w-4 h-4" data-id="element-540" />;
  if (a.includes('coordinat') || a.includes('timing')) return <Clock className="w-4 h-4" data-id="element-541" />;
  if (a.includes('review')) return <Eye className="w-4 h-4" data-id="element-542" />;
  if (a.includes('settle') || a.includes('invoice') || a.includes('pay')) return <Receipt className="w-4 h-4" data-id="element-543" />;
  if (a.includes('launch')) return <Rocket className="w-4 h-4" data-id="element-544" />;
  if (a.includes('kyc') || a.includes('aml') || a.includes('compliance')) return <ShieldCheck className="w-4 h-4" data-id="element-545" />;
  if (a.includes('complete') || a.includes('remaining') || a.includes('admin')) return <ClipboardCheck className="w-4 h-4" data-id="element-546" />;
  return <FileText className="w-4 h-4" data-id="element-547" />;
}
function getDefaultParts(columnIndex: number, status: WorkstreamStatus): WorkstreamPart[] {
  if (status === 'not_started' || status === 'complete') return [];
  // TA column
  if (columnIndex === 0) {
    if (status === 'at_risk') return [{
      name: 'Drafted',
      complete: false
    }, {
      name: 'Invoices',
      complete: false
    }, {
      name: 'Signatures in Escrow',
      complete: false
    }];
    return [{
      name: 'Drafted',
      complete: true
    }, {
      name: 'Invoices',
      complete: false
    }, {
      name: 'Signatures in Escrow',
      complete: false
    }];
  }
  // Onboarding column
  if (columnIndex === 1) {
    if (status === 'at_risk') return [{
      name: 'KYC/AML',
      complete: false
    }, {
      name: 'Sub Docs',
      complete: false
    }, {
      name: 'Tax Forms',
      complete: false
    }];
    return [{
      name: 'KYC/AML',
      complete: true
    }, {
      name: 'Sub Docs',
      complete: false
    }, {
      name: 'Tax Forms',
      complete: false
    }];
  }
  // Diligence column
  if (columnIndex === 2) {
    if (status === 'at_risk') return [{
      name: 'DDQ Responses',
      complete: false
    }, {
      name: 'Tax Withholding',
      complete: false
    }, {
      name: 'ERISA Reps',
      complete: false
    }];
    return [{
      name: 'DDQ Responses',
      complete: true
    }, {
      name: 'Tax Withholding',
      complete: false
    }];
  }
  return [];
}
function StatusPill({
  status,
  label,
  parts,
  columnIndex
}: {
  status: string;
  label: string;
  parts?: WorkstreamPart[];
  columnIndex?: number;
}) {
  const [isHovered, setIsHovered] = useState(false);
  if (status === 'not_started' || label === '—' || label === 'Not Started') {
    return <span className="text-gray-300" data-id="element-548">—</span>;
  }
  let bg = 'bg-gray-100';
  let text = 'text-gray-600';
  if (label === 'Complete' || label === 'Ready') {
    bg = 'bg-emerald-50';
    text = 'text-emerald-700';
  } else if (label === 'Pending') {
    bg = 'bg-amber-50';
    text = 'text-amber-700';
  } else if (label === 'Blocked') {
    bg = 'bg-rose-50';
    text = 'text-rose-700';
  } else if (label === 'Drafting') {
    bg = 'bg-amber-100/50';
    text = 'text-amber-800';
  } else if (label === 'Subscribe') {
    bg = 'bg-indigo-50';
    text = 'text-indigo-700';
  } else if (label === 'Capable') {
    bg = 'bg-emerald-50';
    text = 'text-emerald-700';
  } else if (label === 'Submitted') {
    bg = 'bg-amber-50';
    text = 'text-amber-700';
  }
  // Resolve parts: use explicit parts, or generate defaults for non-complete/non-not_started
  const resolvedParts: WorkstreamPart[] = parts && parts.length > 0 ? parts : (status === 'in_progress' || status === 'at_risk') && columnIndex !== undefined ? getDefaultParts(columnIndex, status as WorkstreamStatus) : [];
  const showBar = resolvedParts.length > 0 && label !== 'Complete' && label !== 'Ready';
  const completedCount = resolvedParts.filter(p => p.complete).length;
  // Segment bar colors
  const segmentComplete = status === 'at_risk' ? 'bg-rose-400' : 'bg-amber-400';
  const segmentIncomplete = status === 'at_risk' ? 'bg-rose-200' : 'bg-amber-200';
  return <div className={`relative w-full flex flex-col items-center ${bg}`} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} data-id="element-549">
      <div className={`w-full py-2.5 px-2 flex items-center justify-center text-xs font-medium ${text}`} data-id="element-550">
        {label}
      </div>
      {showBar && <div className="flex gap-px w-full px-2 pb-1.5" data-id="element-551">
          {resolvedParts.map((part, i) => <div key={i} className={`h-[3px] flex-1 rounded-full transition-colors ${part.complete ? segmentComplete : segmentIncomplete}`} data-id="element-552" />)}
        </div>}
      {/* Tooltip */}
      {isHovered && showBar && <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 z-50 pointer-events-none" data-id="element-553">
          <div className="bg-gray-900 text-white rounded-lg shadow-xl px-3.5 py-2.5 text-left whitespace-nowrap" data-id="element-554">
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5" data-id="element-555">
              {completedCount}/{resolvedParts.length} complete
            </p>
            <div className="flex flex-col gap-1" data-id="element-556">
              {resolvedParts.map((part, i) => <div key={i} className="flex items-center gap-2 text-xs" data-id="element-557">
                  {part.complete ? <Check className="w-3 h-3 text-emerald-400 flex-shrink-0" data-id="element-558" /> : <Circle className="w-3 h-3 text-gray-500 flex-shrink-0" data-id="element-559" />}
                  <span className={part.complete ? 'text-gray-300' : 'text-white'} data-id="element-560">
                    {part.name}
                  </span>
                </div>)}
            </div>
            {/* Caret */}
            <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45" data-id="element-561" />
          </div>
        </div>}
    </div>;
}
function ConsentPill({
  status,
  label
}: {
  status: string;
  label: string;
}) {
  if (status === 'not_started' || label === '—' || label === 'Not Started') {
    return <div className="w-full py-2.5 px-2 flex items-center justify-center gap-2" data-id="element-562">
        <span className="w-2 h-2 rounded-full bg-gray-300" data-id="element-563"></span>
        <span className="text-sm font-semibold text-gray-400" data-id="element-564">—</span>
      </div>;
  }
  let text = 'text-gray-600';
  let dot = 'bg-gray-400';
  let weight = 'font-semibold';
  if (label === 'Ready') {
    text = 'text-emerald-700';
    dot = 'bg-emerald-500';
  } else if (label === 'Pending') {
    text = 'text-amber-700';
    dot = 'bg-amber-500';
  } else if (label === 'Blocked') {
    text = 'text-rose-700';
    dot = 'bg-rose-500';
  }
  return <div className="w-full py-2.5 px-2 flex items-center justify-center gap-2" data-id="element-565">
      <span className={`w-2 h-2 rounded-full ${dot}`} data-id="element-566"></span>
      <span className={`text-sm ${weight} ${text}`} data-id="element-567">{label}</span>
    </div>;
}
export function getActionCounts(projectName: string) {
  const data = MOCK_DATA[projectName] || MOCK_DATA.default;
  if (!data) return {
    total: 0,
    overdue: 0
  };
  const tasks = data.nextActions.flatMap(group => group.tasks.map(task => ({
    ...task,
    gp: group.gp
  })));
  const overdue = tasks.filter(t => t.deadline && new Date(t.deadline) < new Date('2026-04-20')).length;
  return {
    total: tasks.length,
    overdue
  };
}
export function ProjectOverview({
  projectName,
  onGpSelect,
  isPanelOpen: externalPanelOpen,
  onPanelOpenChange
}: {
  projectName: string;
  onGpSelect?: (gp: string, tab?: string) => void;
  isPanelOpen?: boolean;
  onPanelOpenChange?: (open: boolean) => void;
}) {
  const data = MOCK_DATA[projectName] || MOCK_DATA.default;
  const [isStatusCollapsed, setIsStatusCollapsed] = useState(false);
  const [activeActionFilter, setActiveActionFilter] = useState('All');
  const [showAllGPs, setShowAllGPs] = useState(false);
  const [internalPanelOpen, setInternalPanelOpen] = useState(false);
  const isPanelOpen = externalPanelOpen !== undefined ? externalPanelOpen : internalPanelOpen;
  const setIsPanelOpen = (open: boolean) => {
    if (onPanelOpenChange) onPanelOpenChange(open);else setInternalPanelOpen(open);
  };
  const MAX_VISIBLE_ROWS = 8;
  const parseDate = (dateStr: string | null) => {
    if (!dateStr) return Infinity;
    return new Date(dateStr).getTime();
  };
  const flatTasks = data.nextActions.flatMap(group => group.tasks.map(task => ({
    ...task,
    gp: group.gp
  }))).sort((a, b) => parseDate(a.deadline) - parseDate(b.deadline));
  const finalTasks = [...flatTasks];
  // Place dependent tasks after their parent using a safe single-pass approach
  // to avoid infinite loops when multiple tasks share the same parent
  let changed = true;
  let safety = 0;
  while (changed && safety < 200) {
    changed = false;
    safety++;
    for (let i = 0; i < finalTasks.length; i++) {
      const task = finalTasks[i];
      if (task.dependsOnTaskId) {
        const parentIndex = finalTasks.findIndex(t => t.id === task.dependsOnTaskId);
        // Only move if the task appears BEFORE its parent
        if (parentIndex !== -1 && parentIndex > i) {
          finalTasks.splice(i, 1);
          const newParentIndex = finalTasks.findIndex(t => t.id === task.dependsOnTaskId);
          finalTasks.splice(newParentIndex + 1, 0, task);
          changed = true;
          break;
        }
      }
    }
  }
  const filteredTasks = finalTasks.filter(task => {
    if (activeActionFilter === 'All') return true;
    const isDiligence = task.action.toLowerCase().includes('ddq');
    const isTransferAgmt = task.action.toLowerCase().includes('draft') || task.action.toLowerCase().includes('confirm') || task.action.toLowerCase().includes('ta');
    if (activeActionFilter === 'Diligence') return isDiligence;
    if (activeActionFilter === 'Transfer Agmt') return isTransferAgmt;
    if (activeActionFilter === 'Onboarding') return !isDiligence && !isTransferAgmt;
    return true;
  });
  // Compute legend counts dynamically
  const readyCount = data.workstreams.rows.filter(row => row.cells.every(c => c.status === 'complete')).length;
  const blockedCount = data.workstreams.rows.filter(row => row.cells.some(c => c.status === 'at_risk')).length;
  const pendingCount = data.workstreams.rows.filter(row => row.cells.some(c => c.status === 'in_progress') && !row.cells.some(c => c.status === 'at_risk') && !row.cells.every(c => c.status === 'complete')).length;
  const visibleRows = showAllGPs ? data.workstreams.rows : data.workstreams.rows.slice(0, MAX_VISIBLE_ROWS);
  const hasMoreRows = data.workstreams.rows.length > MAX_VISIBLE_ROWS;
  const overdueTasks = finalTasks.filter(task => task.deadline && new Date(task.deadline) < new Date('2026-04-20'));
  const hasOverdue = overdueTasks.length > 0;
  const topActions = [...finalTasks].sort((a, b) => {
    const aOverdue = a.deadline && new Date(a.deadline) < new Date('2026-04-20') ? 1 : 0;
    const bOverdue = b.deadline && new Date(b.deadline) < new Date('2026-04-20') ? 1 : 0;
    if (aOverdue !== bOverdue) return bOverdue - aOverdue;
    return parseDate(a.deadline) - parseDate(b.deadline);
  }).slice(0, 3);
  return <div className="relative" data-id="element-568">
      {/* Top Row: Overall Status & Key Actions Notification */}
      <div className="grid grid-cols-3 gap-6 mb-8 items-start" data-id="element-569">
        {/* Overall Status / Summary */}
        <div className="col-span-2 bg-white rounded-lg p-6 shadow-sm border border-gray-200 h-full" data-id="element-570">
          <div className="flex items-center justify-between mb-3" data-id="element-571">
            <div className="flex items-center gap-2" data-id="element-572">
              <Sparkles className="w-5 h-5 text-amber-500" data-id="element-573" />
              <h3 className="text-base font-bold text-gray-900" data-id="element-574">
                {data.summaryHeading}
              </h3>
            </div>
            <button onClick={() => setIsStatusCollapsed(!isStatusCollapsed)} className="text-sm text-gray-400 hover:text-gray-600 font-medium transition-colors" data-id="element-575">
              {isStatusCollapsed ? 'Expand' : 'Collapse'}
            </button>
          </div>
          {!isStatusCollapsed && <p className="text-gray-600 text-sm leading-relaxed ml-7" data-id="element-576">
              {data.summary}
            </p>}
        </div>

        {/* Key Actions Notification Card */}
        <div onClick={() => setIsPanelOpen(true)} className="col-span-1 bg-white rounded-lg p-6 shadow-sm border border-gray-200 cursor-pointer hover:border-gray-300 hover:shadow-md transition-all group h-full flex flex-col" data-id="element-577">
          <div className="flex items-center justify-between mb-4" data-id="element-578">
            <div className="flex items-center gap-2" data-id="element-579">
              <Bell className="w-5 h-5 text-gray-700" data-id="element-580" />
              <h3 className="text-base font-bold text-gray-900" data-id="element-581">Actions</h3>
            </div>
            <span className="text-sm font-semibold text-white bg-[#458b85] px-2 py-0.5 rounded-full" data-id="element-582">
              {finalTasks.length} open
            </span>
          </div>

          <div className="flex flex-col gap-3 flex-1" data-id="element-583">
            {topActions.map(task => {
            const isOverdue = task.deadline && new Date(task.deadline) < new Date('2026-04-20');
            return <div key={task.id} className="flex flex-col gap-1" data-id="element-584">
                  <div className="flex items-center gap-2" data-id="element-585">
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isOverdue ? 'bg-rose-500' : 'bg-amber-400'}`} data-id="element-586"></span>
                    <p className="text-sm text-gray-800 font-medium truncate" data-id="element-587">
                      {task.action}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 pl-3.5" data-id="element-588">
                    <span className="text-xs text-gray-500 bg-gray-50 px-1.5 py-0.5 rounded border border-gray-100 truncate max-w-[120px]" data-id="element-589">
                      {task.gp}
                    </span>
                    {isOverdue && <span className="text-[10px] font-bold uppercase tracking-wider text-rose-600" data-id="element-590">
                        Overdue
                      </span>}
                  </div>
                </div>;
          })}
            {finalTasks.length > 3 && <div className="mt-auto pt-2" data-id="element-591">
                <span className="text-sm font-medium text-[#458b85] group-hover:text-[#3a7570] transition-colors" data-id="element-592">
                  +{finalTasks.length - 3} more actions &rarr;
                </span>
              </div>}
            {finalTasks.length === 0 && <div className="text-sm text-gray-500 italic mt-2" data-id="element-593">
                No open action items.
              </div>}
          </div>
        </div>
      </div>

      {/* Workstreams (Full Width) */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col overflow-hidden" data-id="element-594">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200" data-id="element-595">
          <div className="flex items-center gap-3" data-id="element-596">
            <h3 className="text-base font-bold text-gray-900" data-id="element-597">Workstreams</h3>
            <span className="text-sm text-gray-400" data-id="element-598">
              {data.workstreams.rows.length} GPs
            </span>
          </div>
          <div className="flex items-center gap-4 text-xs text-gray-500 font-medium" data-id="element-599">
            <div className="flex items-center gap-1.5" data-id="element-600">
              <span className="w-2 h-2 rounded-full bg-emerald-500" data-id="element-601"></span>
              {readyCount} ready
            </div>
            <div className="flex items-center gap-1.5" data-id="element-602">
              <span className="w-2 h-2 rounded-full bg-rose-500" data-id="element-603"></span>
              {blockedCount} blocked
            </div>
            <div className="flex items-center gap-1.5" data-id="element-604">
              <span className="w-2 h-2 rounded-full bg-amber-500" data-id="element-605"></span>
              {pendingCount} pending
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto" data-id="element-606">
          <table className="w-full text-left border-collapse table-fixed" data-id="element-607">
            <colgroup data-id="element-608">
              <col className="w-[28%]" data-id="element-609" />
              {data.workstreams.columns.map(col => <col key={`col-${col}`} className="w-[18%]" data-id="element-610" />)}
              <col className="w-[18%]" data-id="element-611" />
            </colgroup>
            <thead data-id="element-612">
              {/* Accent bar row */}
              <tr data-id="element-613">
                <th className="p-0 h-1" data-id="element-614"></th>
                {data.workstreams.columns.map(col => <th key={`bar-${col}`} className="p-0 h-1" data-id="element-615">
                    <div className="h-[3px] bg-gray-900 mx-0.5" data-id="element-616"></div>
                  </th>)}
                <th className="p-0 h-1" data-id="element-617"></th>
              </tr>
              <tr className="border-b border-gray-200" data-id="element-618">
                <th className="py-4 px-5 text-xs font-semibold text-gray-400 uppercase tracking-wider" data-id="element-619">
                  GP
                </th>
                {data.workstreams.columns.map(col => <th key={col} className="py-4 px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider text-center" data-id="element-620">
                    {col === 'TA' ? 'Transfer Agmt' : col}
                  </th>)}
                <th className="py-4 px-5 text-xs font-semibold text-gray-400 uppercase tracking-wider text-center border-l border-gray-200" data-id="element-621">
                  GP Consent
                </th>
              </tr>
            </thead>
            <tbody data-id="element-622">
              {visibleRows.map((row, idx) => {
              // Calculate consent status
              const isAllComplete = row.cells.every(c => c.status === 'complete');
              const isAnyAtRisk = row.cells.some(c => c.status === 'at_risk');
              const isAllNotStarted = row.cells.every(c => c.status === 'not_started');
              let consentStatus = 'in_progress';
              let consentLabel = 'Pending';
              if (isAllComplete) {
                consentStatus = 'complete';
                consentLabel = 'Ready';
              } else if (isAnyAtRisk) {
                consentStatus = 'at_risk';
                consentLabel = 'Blocked';
              } else if (isAllNotStarted) {
                consentStatus = 'not_started';
                consentLabel = '—';
              }
              // GP Count Badge
              const gpCount = row.gp === 'EQT' || row.gp === 'EQT Partners' ? 4 : row.gp === 'Carlyle Group' || row.gp === 'KKR & Co' ? 3 : row.gp === 'Blackstone' || row.gp === 'Apollo Global Management' ? 2 : 1;
              return <tr key={row.gp} className="border-b border-gray-100 last:border-0 hover:bg-gray-50/50 transition-colors" data-id="element-623">
                    <td className="py-4 px-5 cursor-pointer" onClick={() => onGpSelect?.(row.gp)} data-id="element-624">
                      <div className="flex items-center gap-2" data-id="element-625">
                        <GpBadge name={row.gp} data-id="element-626" />
                        <span className="inline-flex items-center justify-center bg-gray-100 text-gray-500 text-[10px] font-bold rounded px-1.5 py-0.5" data-id="element-627">
                          {gpCount}
                        </span>
                      </div>
                    </td>
                    {row.cells.map((cell, cIdx) => {
                  const colName = data.workstreams.columns[cIdx];
                  const tabName = colName === 'TA' ? 'Transfer Agreement' : colName === 'Onboarding' ? 'Onboarding' : colName === 'Diligence' ? 'Diligence' : colName;
                  return <td key={cIdx} className="p-0 text-center border-l border-gray-50 cursor-pointer" onClick={() => onGpSelect?.(row.gp, tabName)} data-id="element-628">
                          <StatusPill status={cell.status} label={cell.label} parts={cell.parts} columnIndex={cIdx} data-id="element-629" />
                        </td>;
                })}
                    <td className="p-0 text-center border-l border-gray-200 cursor-pointer" onClick={() => onGpSelect?.(row.gp)} data-id="element-630">
                      <ConsentPill status={consentStatus} label={consentLabel} data-id="element-631" />
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>

        {/* Show All CTA */}
        {hasMoreRows && <button onClick={() => setShowAllGPs(!showAllGPs)} className="flex items-center justify-center gap-2 py-3.5 border-t border-gray-200 text-sm font-medium text-[#458b85] hover:bg-gray-50 transition-colors" data-id="element-632">
            <span data-id="element-633">
              {showAllGPs ? 'Show fewer' : `Show all ${data.workstreams.rows.length} GPs`}
            </span>
            <ChevronDown className={`w-4 h-4 transition-transform ${showAllGPs ? 'rotate-180' : ''}`} data-id="element-634" />
          </button>}
      </div>

      {/* Slide-out Panel for Key Actions */}
      {createPortal(<AnimatePresence data-id="element-635">
          {isPanelOpen && <div className="fixed inset-0 z-[100] flex justify-end" data-id="element-636">
              {/* Backdrop */}
              <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setIsPanelOpen(false)} className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm" data-id="element-637" />

              {/* Panel */}
              <motion.div initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="relative w-full max-w-md bg-white h-full shadow-2xl flex flex-col border-l border-gray-200" data-id="element-638">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50" data-id="element-639">
                  <div className="flex items-center gap-3" data-id="element-640">
                    <div className="p-2 bg-white rounded-lg shadow-sm border border-gray-200" data-id="element-641">
                      <Bell className="w-5 h-5 text-gray-700" data-id="element-642" />
                    </div>
                    <div data-id="element-643">
                      <h3 className="text-lg font-bold text-gray-900" data-id="element-644">
                        Actions
                      </h3>
                      <p className="text-sm text-gray-500" data-id="element-645">
                        {finalTasks.length} open items
                      </p>
                    </div>
                  </div>
                  <button onClick={() => setIsPanelOpen(false)} className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-full transition-colors" data-id="element-646">
                    <X className="w-5 h-5" data-id="element-647" />
                  </button>
                </div>

                {/* Filters */}
                <div className="px-6 py-4 border-b border-gray-100 flex gap-2 flex-wrap bg-white" data-id="element-648">
                  {['All', 'Transfer Agmt', 'Onboarding', 'Diligence'].map(f => <button key={f} onClick={() => setActiveActionFilter(f)} className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${activeActionFilter === f ? 'bg-gray-100 border-gray-200 text-gray-800' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700'}`} data-id="element-649">
                        {f}
                      </button>)}
                </div>

                {/* Actions List */}
                <div className="flex-1 overflow-y-auto bg-white" data-id="element-650">
                  <div className="flex flex-col" data-id="element-651">
                    {filteredTasks.map((task, idx) => {
                const isOverdue = task.deadline && new Date(task.deadline) < new Date('2026-04-20');
                const isDiligence = task.action.toLowerCase().includes('ddq');
                const isTransferAgmt = task.action.toLowerCase().includes('draft') || task.action.toLowerCase().includes('confirm') || task.action.toLowerCase().includes('ta');
                const category = isDiligence ? 'Diligence' : isTransferAgmt ? 'Transfer Agmt' : 'Onboarding';
                const categoryColor = isDiligence ? 'text-amber-700' : isTransferAgmt ? 'text-blue-700' : 'text-teal-700';
                return <div key={task.id} className="p-6 border-b border-gray-100 last:border-0 hover:bg-gray-50/50 transition-colors" data-id="element-652">
                          <div className="flex items-start gap-3 mb-3" data-id="element-653">
                            <span className={`mt-0.5 flex-shrink-0 ${categoryColor}`} data-id="element-654">
                              {getActionIcon(task.action)}
                            </span>
                            <p className="text-sm font-medium text-gray-900 leading-snug" data-id="element-655">
                              <SourceAnnotation sourceType="email" sourceTitle="Re: Next Steps & Action Items" sourceDate="Today, 10:15 AM" extractedValue={task.action} context="Task Action Item" data-id="element-656">
                                {task.action}
                              </SourceAnnotation>
                            </p>
                          </div>

                          <div className="ml-7 flex flex-col gap-2.5" data-id="element-657">
                            <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap" data-id="element-658">
                              <span className={`${categoryColor} font-medium bg-white px-1.5 py-0.5 rounded border border-gray-100`} data-id="element-659">
                                {category}
                              </span>
                              <span className="w-0.5 h-0.5 rounded-full bg-gray-300" data-id="element-660"></span>
                              <GpBadge name={task.gp} className="text-xs" onClick={() => {
                        setIsPanelOpen(false);
                        onGpSelect?.(task.gp);
                      }} data-id="element-661" />
                            </div>

                            <div className="flex items-center gap-3 text-xs" data-id="element-662">
                              <div className="flex items-center gap-1.5 text-gray-500" data-id="element-663">
                                <User className="w-3.5 h-3.5" data-id="element-664" />
                                <SourceAnnotation sourceType="email" sourceTitle="Re: Next Steps & Action Items" sourceDate="Today, 10:15 AM" extractedValue={task.owner || 'TBD'} context="Task Owner" data-id="element-665">
                                  {task.owner || 'TBD'}
                                </SourceAnnotation>
                              </div>

                              {task.deadline && <>
                                  <span className="w-0.5 h-0.5 rounded-full bg-gray-300" data-id="element-666"></span>
                                  <div className={`flex items-center gap-1.5 ${isOverdue ? 'text-rose-600 font-medium' : 'text-gray-500'}`} data-id="element-667">
                                    <CalendarDays className="w-3.5 h-3.5" data-id="element-668" />
                                    {isOverdue ? 'Overdue · ' : 'Due '}
                                    <SourceAnnotation sourceType="email" sourceTitle="Re: Next Steps & Action Items" sourceDate="Today, 10:15 AM" extractedValue={task.deadline} context="Task Deadline" data-id="element-669">
                                      {task.deadline}
                                    </SourceAnnotation>
                                  </div>
                                </>}
                            </div>
                          </div>
                        </div>;
              })}
                    {filteredTasks.length === 0 && <div className="p-12 text-center text-sm text-gray-500" data-id="element-670">
                        No actions match the selected filter.
                      </div>}
                  </div>
                </div>
              </motion.div>
            </div>}
        </AnimatePresence>, document.body)}
    </div>;
}